import clsx from 'clsx';
import { forwardRef } from 'react';

import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { type WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { ellipsis } from '../../styles/ellipsis.sty.js';
import { textStyle } from '../../styles/textStyle.sty.js';
import { useSharedProgressBarPropsContext } from './hooks/useProgressBarPropsContext.js';
import { label } from './ProgressBarLabel.sty.js';

/**
 * Accepted properties for ProgressBarLabel.
 * @public
 */
export interface ProgressBarLabelProps
  extends WithChildren, StylingProps, DataTestId {}

/**
 * ProgressBarLabel sub-component is rendered inside ProgressBar component.
 * @public
 */
export const ProgressBarLabel = forwardRef<
  HTMLLabelElement,
  ProgressBarLabelProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: ProgressBarLabelProps = props;

  const { id, density } = useSharedProgressBarPropsContext();

  const childIsText = canReceiveDefaultTextFormat(children);

  return (
    <label
      data-testid={dataTestId}
      ref={forwardedRef}
      htmlFor={id}
      className={clsx(
        {
          [ellipsis]: density === 'condensed' && childIsText,
        },
        consumerClassName,
        label({ size: density, childIsText }),
        textStyle({ textStyle: 'base' }),
      )}
      style={consumerStyle}
      {...remainingProps}
    >
      {children}
    </label>
  );
});

(
  ProgressBarLabel as typeof ProgressBarLabel & { displayName: string }
).displayName = 'ProgressBar.Label';
